import { useCallback, useEffect, useMemo, useState } from "react";
import clinicAPI from "../../../services/clinicApi";
import { ageFromDob } from "../../../utils/clientAppUtils";
import { useAdminHubUi } from "../../../hooks/useAdminAppMode";
import {
  clientProfileSubtitle,
  clientStatusBadges,
} from "./clientSportDisplay";
import { tagPresetLabel } from "../../../constants/clientProfileMeta";
import AdminGuardiansPanel from "./AdminGuardiansPanel";
import aa from "../AdminPortal/adminApp.module.css";
import s from "../clinic.module.css";
import { mediaUrl } from "../../../utils/clientHeroTheme";
import { Avatar } from "../../ui";

export default function ClientProfileHero({
  patient,
  patientId,
  sport,
  edit,
  onMessage,
  onArchive,
  showGuardiansDetail,
  onToggleGuardians,
}) {
  const isApp = useAdminHubUi();
  const [guardians, setGuardians] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(null);

  useEffect(() => {
    setPhotoUrl(patient?.photo_url || null);
  }, [patient?.photo_url, patientId]);

  const loadGuardians = useCallback(async () => {
    if (!patientId) return;
    try {
      const data = await clinicAPI.getAthleteGuardians(patientId);
      setGuardians(data.guardians || []);
    } catch {
      setGuardians([]);
    }
  }, [patientId]);

  useEffect(() => {
    loadGuardians();
  }, [loadGuardians]);

  const card = patient?.patient_card;
  const archived = card?.is_archived;
  const age = ageFromDob(patient?.date_of_birth);
  const headerBadges = clientStatusBadges(sport);
  const subtitle = clientProfileSubtitle(sport);

  const tags = useMemo(() => {
    const list = headerBadges.map((b) => ({ key: b.label, label: b.label, tone: b.tone }));
    (edit?.tags || card?.tags || []).forEach((id) => {
      list.push({ key: `tag-${id}`, label: tagPresetLabel(id), tone: "blue" });
    });
    if (edit?.lead_source?.trim() || card?.lead_source) {
      list.push({
        key: "lead",
        label: edit?.lead_source || card?.lead_source,
        tone: "muted",
      });
    }
    if (edit?.allergies?.trim()) {
      list.push({ key: "allergy", label: "Аллергия", tone: "warn" });
    }
    return list;
  }, [headerBadges, edit?.allergies, edit?.tags, edit?.lead_source, card?.tags, card?.lead_source]);

  const statusLabel = archived
    ? "В архиве"
    : patient?.is_blocked
      ? "Заблокирован"
      : "Активен";

  const statusClass = isApp
    ? archived
      ? aa.clientHeroStatusArchived
      : patient?.is_blocked
        ? aa.clientHeroStatusBlocked
        : aa.clientHeroStatusActive
    : archived
      ? s.clientHeroStatusArchived
      : patient?.is_blocked
        ? s.clientHeroStatusBlocked
        : s.clientHeroStatusActive;

  const canMessage = true;

  return (
    <header className={isApp ? aa.clientHero : s.clientProfileHero}>
      <div className={isApp ? aa.clientHeroMain : s.clientProfileHeroMain}>
        <div className={isApp ? aa.clientHeroAvatarStatic : s.clientHeroAvatarStatic}>
          <Avatar
            name={patient?.full_name}
            src={mediaUrl(photoUrl)}
            size="lg"
          />
        </div>
        <div className={isApp ? aa.clientHeroText : s.clientProfileHeroText}>
          <h1 className={isApp ? aa.clientHeroName : s.clientProfileHeroName}>
            {patient?.full_name || "Клиент"}
          </h1>
          <p className={isApp ? aa.clientHeroMeta : s.clientProfileHeroMeta}>
            {age != null ? <span>{age} лет</span> : null}
            <span className={statusClass}>{statusLabel}</span>
          </p>
          {subtitle ? (
            <p className={isApp ? aa.clientHeroSubline : s.clientProfileHeroSubline}>
              {subtitle}
            </p>
          ) : null}
          {tags.length > 0 ? (
            <div className={isApp ? aa.clientHeroTags : s.clientProfileHeroTags}>
              {tags.map((t) => (
                <span
                  key={t.key}
                  className={
                    isApp
                      ? `${aa.clientHeroTag} ${aa[`clientHeroTag_${t.tone}`] || ""}`
                      : `${s.clientProfileHeroTag} ${s[`clientProfileHeroTag_${t.tone}`] || ""}`
                  }
                >
                  {t.label}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <div
        className={
          isApp ? aa.clientHeroActions : s.clientProfileHeroActionsCompact
        }
      >
        {patient?.phone ? (
          <a
            href={`tel:${patient.phone}`}
            className={
              isApp ? aa.clientHeroActionPrimary : s.clientProfileHeroActionPrimary
            }
          >
            Позвонить
          </a>
        ) : null}
        {canMessage ? (
          <button
            type="button"
            className={
              isApp ? aa.clientHeroActionGhost : s.clientProfileHeroActionGhost
            }
            onClick={onMessage}
          >
            Написать
          </button>
        ) : null}
        <div className={isApp ? aa.clientHeroMenuWrap : s.clientProfileHeroMenuWrap}>
          <button
            type="button"
            className={isApp ? aa.clientHeroMenuBtn : s.clientProfileHeroMenuBtn}
            onClick={() => setMenuOpen((v) => !v)}
            aria-expanded={menuOpen}
            aria-label="Ещё действия"
          >
            ⋯
          </button>
          {menuOpen ? (
            <div className={isApp ? aa.clientHeroMenu : s.clientProfileHeroMenu}>
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  onToggleGuardians();
                }}
              >
                {guardians.length
                  ? `Родители (${guardians.length})`
                  : "Добавить родителя"}
              </button>
              {!archived ? (
                <button
                  type="button"
                  className={
                    isApp ? aa.clientHeroMenuDanger : s.clientProfileHeroMenuDanger
                  }
                  onClick={() => {
                    setMenuOpen(false);
                    onArchive();
                  }}
                >
                  В архив
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {showGuardiansDetail ? (
        <div
          className={
            isApp ? aa.clientHeroGuardiansPanel : s.clientProfileHeroGuardiansPanel
          }
        >
          <AdminGuardiansPanel patientId={patientId} onChanged={loadGuardians} />
        </div>
      ) : null}
    </header>
  );
}
